import React from 'react';
import PropTypes from 'prop-types';
import { Button, Typography } from '@material-ui/core';
import { ArchiveOutlined, SettingsBackupRestore } from '@material-ui/icons';
import { makeStyles } from '@material-ui/styles';
import CustomTable from '../../components/CustomTable';
import CellaTesto from './CellaTesto';

const useStyles = makeStyles((theme) => ({
  cella: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: theme.spacing(1),
  },
  sonda: {
    fontFamily: 'monospace',
  },
  // Controllo column: placeholder name on top, trace sentence underneath.
  // `rt-td` forces `white-space: nowrap`, so without `normal` the sentence is cut
  // at the column edge, mid-word and with no ellipsis — and what would drop is
  // exactly the part that says *why* the voce is open.
  provenienza: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    width: '100%',
  },
  traccia: {
    color: theme.palette.text.secondary,
  },
  // Archiving is not reassigning: the probe stays without a Controllo, and the
  // operator is declaring that the fridge is no longer watched. Distinct colour
  // above the distinct label, as for Disassocia (ADR-0010).
  archivia: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
  messaggio: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
  },
}));

/**
 * The review queue of the orphaned Assegnazioni (US 33, BSDEVPLAT-111).
 *
 * A row here is a probe the publication stopped making watch anything: its
 * subject does not exist in the published manual, so the fridge is unguarded and
 * no alarm will say so. The columns carry the trace's **old** ids, which may not
 * resolve any more — see `rimappatureUtils`.
 */
const CodaRevisioneTable = ({
  errore,
  loading,
  onArchivia,
  onRiassegna,
  voci,
}) => {
  const classes = useStyles();

  const columns = [
    {
      Header: 'Sonda',
      id: 'sonda',
      minWidth: 150,
      accessor: (riga) => riga.sn,
      Cell: ({ original }) => (
        <CellaTesto testo={original.sn} className={classes.sonda} />
      ),
    },
    {
      Header: 'Attrezzatura',
      id: 'attrezzatura',
      minWidth: 180,
      accessor: (riga) => riga.attrezzaturaEtichetta,
      Cell: ({ original }) => (
        <CellaTesto testo={original.attrezzaturaEtichetta} />
      ),
    },
    {
      Header: 'Controllo di provenienza',
      id: 'controllo',
      minWidth: 260,
      accessor: (riga) => riga.controlloEtichetta,
      Cell: ({ original }) => (
        <div className={classes.cella}>
          {/* The trace's old ids live in the tooltip and nowhere else: support
              needs them, the operator has nothing to do with a renumbered id of
              a retired revision (see `descriviIdProvenienza`). */}
          <div
            className={classes.provenienza}
            title={original.idProvenienzaEtichetta ?? undefined}
          >
            <Typography variant="body2" component="span">
              {original.controlloEtichetta}
            </Typography>
            {/* The trace sentence, not a rewrite of it: it says whether the
                attrezzatura or the Controllo went missing, which is the only
                thing that orients whoever has to choose between reassigning and
                archiving. */}
            {original.descrizione && (
              <Typography
                variant="caption"
                component="span"
                className={classes.traccia}
              >
                {original.descrizione}
              </Typography>
            )}
          </div>
        </div>
      ),
    },
    {
      Header: 'Revisione del manuale',
      id: 'revisione',
      width: 170,
      accessor: (riga) => riga.revisioneEtichetta,
      Cell: ({ original }) => (
        <CellaTesto testo={original.revisioneEtichetta} />
      ),
    },
    {
      Header: 'Istante',
      id: 'istante',
      width: 160,
      accessor: (riga) => riga.creatoIl,
      Cell: ({ original }) => <CellaTesto testo={original.istanteEtichetta} />,
    },
    {
      Header: 'Revisione',
      id: 'azioni',
      minWidth: 260,
      sortable: false,
      accessor: (riga) => riga,
      Cell: ({ original }) => (
        <div className={classes.cella}>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            startIcon={<SettingsBackupRestore />}
            onClick={() => onRiassegna(original)}
          >
            Riassegna
          </Button>
          <Button
            size="small"
            variant="outlined"
            className={classes.archivia}
            startIcon={<ArchiveOutlined />}
            onClick={() => onArchivia(original)}
          >
            Archivia
          </Button>
        </div>
      ),
    },
  ];

  // An unreadable queue replaces the table instead of sitting under it. Rendered
  // together, `CustomTable` prints its own «Nessun record» above the sentence,
  // and that first line is the one the eye lands on: silence read as «everything
  // is fine» is the failure this block exists to avoid.
  if (!loading && errore) {
    return (
      <Typography variant="body2" component="p" className={classes.messaggio}>
        {errore}
      </Typography>
    );
  }

  return (
    <>
      <CustomTable
        records={voci}
        columns={columns}
        loading={loading}
        filterable={false}
        resizable={true}
        sortable={true}
        defaultPageSize={5}
      />
      {/* Three distinct states, not two: while loading nothing is claimed, on an
          empty queue the emptiness is asserted, and on an error the sentence
          above says it is not known. An empty queue and a queue not yet loaded
          read alike if the emptiness is not spelled out. */}
      {!loading && voci.length === 0 && (
        <Typography variant="body2" component="p" className={classes.messaggio}>
          Nessuna assegnazione da rivedere: l’ultima pubblicazione non ha
          lasciato sonde senza controllo.
        </Typography>
      )}
    </>
  );
};

CodaRevisioneTable.propTypes = {
  errore: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  onArchivia: PropTypes.func.isRequired,
  onRiassegna: PropTypes.func.isRequired,
  voci: PropTypes.array.isRequired,
};

export default CodaRevisioneTable;
